/**
 * useOrdersListing — orders-list driver.
 *
 * Bundles the listing wiring (useListing config, sort/search shims, provider
 * shape) so any component can drive an orders list. Opt into `provide: true`
 * to expose the legacy `ordersProvider` / `ordersProviderMethods` injection
 * keys consumed by `useOrdersInjector`. Filter defs (and their lazy
 * custom-field fetch wiring) live in `useOrderFilters`.
 */
import {
  ref,
  computed,
  watch,
  unref,
  nextTick,
  onBeforeUnmount,
  provide as vueProvide
} from "vue";
import type { Ref, ComputedRef } from "vue";
import { DataModules } from "@/store/modules/data/modules";
import { useListing, buildSegmentFilterParams } from "@/composables/listing";
import type {
  FilterDef,
  ScopingParam,
  ListingState
} from "@/composables/listing";
import { useContext } from "@/composables/context";
import { useOrderFilters } from "./useOrderFilters";
import store from "@/store";
import lodash from "@/boot/lodash";
import { Sorters } from "@/data/table";
import type { ISorter, IFilter } from "@/models/tables";
import type { ITag, IClient, IOrder } from "@upmind-automation/types";
import type {
  IOrdersProvider,
  IOrdersProviderMethods
} from "@/models/providers/orders";

const MANUAL_FILTER_KEY_RE = /^filter\[([^|\]]+)(?:\|([^\]]+))?\]$/;

type MaybeRef<T> = T | Ref<T> | ComputedRef<T>;

export interface UseOrdersListingOptions {
  /** Listing instance id (drives URL persist key & store scope) */
  id?: string;
  /** When set, scopes the list to a single client */
  clientId?: MaybeRef<IClient["id"] | null>;
  /** Extra hard-coded filters merged into scopingParams */
  manualFilters?: MaybeRef<Record<string, any>>;
  /** Page size */
  limit?: MaybeRef<number>;
  /** Sorter definitions for the UI's sort dropdown */
  availableSorters?: MaybeRef<ISorter[]>;
  enableFiltering?: boolean;
  enableSorting?: boolean;
  /**
   * When true, exposes the `ordersProvider` / `ordersProviderMethods`
   * injection keys so descendants using `useOrdersInjector` resolve them.
   */
  provide?: boolean;
  /** When true, fetches once during setup if `contentIsAvailable`. */
  autoLoad?: boolean;
}

export interface UseOrdersListingReturn {
  listing: ListingState;
  /** `{ field, direction }` derived from `listing.activeSort`. */
  activeSort: ComputedRef<{ field: string; direction: Sorters }>;
  /** True for every in-flight fetch (initial/reload/filter/sort/page/search). */
  isFetching: ComputedRef<boolean>;
  defaults: {
    page: number;
    limit: number;
    sort: { field: string; direction: Sorters };
    filters: IFilter[];
    constFilters: IFilter[];
  };
  sorters: Ref<ISorter[]>;
  isLoadingLocal: Ref<boolean>;
  isReloadingLocal: Ref<boolean>;
  contentIsAvailable: ComputedRef<boolean>;
  guardFunctionalities: ComputedRef<string[]>;
  /** Bare-key filter map for segment storage (active + locked filters). */
  apiFilters: ComputedRef<Record<string, any>>;
  loadData: () => Promise<void>;
  setSorterDefs: (defs: ISorter[]) => void;
  doQuickSearch: (value?: string) => Promise<void>;
  onSort: (field: string, direction: string) => void;
  applyTagFilter: (tag: ITag) => void;
  onFiltersChange: (filters: IFilter[]) => void;
  debounceSearch: ReturnType<typeof lodash.debounce>;
  /** Builds the `IOrdersProvider` data shape consumed via `useOrdersInjector`. */
  ordersProviderFn: () => IOrdersProvider;
  /** Methods object matching `IOrdersProviderMethods`. */
  ordersProviderMethodsObj: IOrdersProviderMethods;
}

function toComputed<T>(
  input: MaybeRef<T> | undefined,
  fallback: T
): ComputedRef<T> {
  return computed(() => {
    if (input === undefined) return fallback;
    return unref(input) as T;
  });
}

export function useOrdersListing(
  options: UseOrdersListingOptions = {}
): UseOrdersListingReturn {
  const id = options.id ?? "OLP";
  const enableFiltering = options.enableFiltering ?? true;
  const enableSorting = options.enableSorting ?? true;

  const clientIdRef = toComputed<IClient["id"] | null>(options.clientId, null);
  const manualFiltersRef = toComputed<Record<string, any>>(
    options.manualFilters,
    {}
  );
  const limitRef = toComputed<number>(options.limit, 10);
  const availableSortersRef = toComputed<ISorter[]>(
    options.availableSorters,
    []
  );

  const ctx = useContext();

  const sorters = ref<ISorter[]>(availableSortersRef.value);
  const isLoadingLocal = ref(false);
  const isReloadingLocal = ref(false);

  const { allFilters, attachLazyFetch } = useOrderFilters({
    clientId: clientIdRef
  });

  const isMultibrand = store.getters["brand/isMultibrand"];
  const listing = useListing({
    id,
    module: DataModules.ORDERS,
    with: [
      "tags",
      "client",
      "client.image",
      "status",
      "products",
      ...(isMultibrand ? ["brand"] : [])
    ],
    withCount: ["products"],
    // Parse once per manualFilters identity change — the resulting array
    // flows into useListing's deep-hashed scopingParams, so avoid re-parsing
    // when only clientId changes.
    // Manual filters typically use the wrapped `filter[field]` (or
    // `filter[field|op]`) form. Strip the wrapper so `field` matches a
    // FilterDef — that surfaces it as a locked pill and disables the def in
    // the picker, preventing duplicate user-applied filters.
    scopingParams: (() => {
      const parsedManual = computed<ScopingParam[]>(() => {
        const manual = manualFiltersRef.value;
        if (!manual) return [];
        const out: ScopingParam[] = [];
        for (const [key, value] of Object.entries(manual)) {
          const m = key.match(MANUAL_FILTER_KEY_RE);
          if (m) {
            out.push({
              field: m[1],
              operator: m[2] || "eq",
              value,
              locked: true
            });
          } else {
            out.push({ field: key, value });
          }
        }
        return out;
      });
      return computed<ScopingParam[]>(() => {
        const params: ScopingParam[] = [];
        if (clientIdRef.value) {
          params.push({ field: "client_id", value: clientIdRef.value });
        }
        params.push(...parsedManual.value);
        return params;
      });
    })(),
    // Debounce is handled by the outer `debounceSearch` wrapper (the provider
    // interface exposes a DebouncedFunc to templates) — keep listing's
    // internal debounce at 0 to avoid compounding latency.
    search: { field: "number", debounce: 0 },
    sort: "-created_at",
    limit: limitRef.value,
    persist: "url",
    filters: allFilters
  });

  const activeSort = computed(() => {
    const sort = listing.activeSort.value;
    return {
      field: sort.replace(/^-/, ""),
      direction: sort.startsWith("-") ? Sorters.DESCENDING : Sorters.ASCENDING
    };
  });

  const isFetching = computed(() => listing.isFetching.value);

  const defaults = {
    page: 1,
    limit: limitRef.value,
    sort: { field: "created_at", direction: Sorters.DESCENDING },
    filters: [] as IFilter[],
    constFilters: [] as IFilter[]
  };

  const guardFunctionalities = computed<string[]>(
    () => store.getters[`data/${DataModules.ORDERS}/listFunctionalities`]
  );

  const contentIsAvailable = computed<boolean>(
    () =>
      ctx.isClient.value ||
      store.getters["user/can"](...(guardFunctionalities.value || []))
  );

  function doQuickSearch(value = "") {
    listing.search.value = value;
    return Promise.resolve();
  }

  function onSort(field: string, direction: string) {
    const sort = field
      ? `${direction === Sorters.DESCENDING ? "-" : ""}${field}`
      : "-created_at";
    listing.setSort(sort);
  }

  const debounceSearch = lodash.debounce(doQuickSearch, 250);

  const apiFilters = computed<Record<string, any>>(() =>
    buildSegmentFilterParams(
      [...listing.activeFilters.value, ...listing.lockedFilters.value],
      allFilters.value
    )
  );

  function applyTagFilter(tag: ITag) {
    const defs: FilterDef[] = listing.filterDefs.value;
    const clientTagDef = defs.find(d => d.field === "client.tags.id");
    const itemTagDef = defs.find(d => d.field === "products.tags.id");
    const def = clientTagDef || itemTagDef;
    if (!def) return;
    listing.addFilter({
      defId: def.id,
      field: def.field,
      operator: def.defaultOperator,
      value: [tag.id],
      label: def.label
    });
  }

  function onFiltersChange(filters: IFilter[]) {
    const next = (filters || []).map((f: IFilter) => {
      const def = allFilters.value.find(d => d.field === f.key);
      return {
        id: "",
        defId: def?.id || `raw:${f.key}`,
        field: f.key,
        operator: def?.defaultOperator || "eq",
        value: f.value
      };
    });
    listing.setFilters(next);
  }

  function ordersProviderFn(): IOrdersProvider {
    return {
      activeSort: activeSort.value,
      apiFilters: apiFilters.value,
      availableSorters: sorters.value,
      clientId: clientIdRef.value,
      defaults,
      enableFiltering,
      enableSorting,
      guardFunctionalities: guardFunctionalities.value,
      hasOrders: !!listing.data.value.length,
      isFetching: isFetching.value,
      isLoading: isLoadingLocal.value,
      isReloading: isReloadingLocal.value,
      limit: listing.limit.value,
      orders: listing.data.value as IOrder[],
      page: listing.page.value,
      searchQuery: listing.search.value,
      total: listing.total.value
    };
  }

  const ordersProviderMethodsObj: IOrdersProviderMethods = {
    getData: () => listing.fetch(),
    doQuickSearch,
    onFiltersChange,
    onLimitChange: (limit: number) => listing.setLimit(limit),
    onPageChange: (page: number) => listing.setPage(page),
    onSort,
    reloadData: async () => {
      try {
        isReloadingLocal.value = true;
        return await listing.reload();
      } finally {
        isReloadingLocal.value = false;
      }
    },
    applyTagFilter,
    debounceSearch
  };

  function setSorterDefs(defs: ISorter[]) {
    sorters.value = defs;
  }

  async function loadData() {
    try {
      isLoadingLocal.value = true;
      await listing.fetch();
    } finally {
      isLoadingLocal.value = false;
    }
  }

  attachLazyFetch(listing);

  if (options.provide) {
    vueProvide("ordersProvider", ordersProviderFn);
    vueProvide("ordersProviderMethods", ordersProviderMethodsObj);
  }

  // Defer autoLoad to nextTick so any `flush: "post"` scopingParams watchers
  // inside useListing get their first chance to fire before the initial
  // fetch — otherwise a reactive prop resolving during mount can trigger a
  // redundant second fetch right after autoLoad's.
  if (options.autoLoad && contentIsAvailable.value) {
    nextTick(() => {
      if (contentIsAvailable.value) loadData();
    });
  }

  watch(limitRef, next => {
    if (listing.limit.value !== next) listing.setLimit(next);
  });

  // setSorterDefs() can override, but a reactive input wins when untouched.
  watch(availableSortersRef, next => {
    sorters.value = next || [];
  });

  onBeforeUnmount(() => {
    debounceSearch.cancel();
  });

  return {
    listing,
    activeSort,
    isFetching,
    defaults,
    sorters,
    isLoadingLocal,
    isReloadingLocal,
    contentIsAvailable,
    guardFunctionalities,
    apiFilters,
    loadData,
    setSorterDefs,
    doQuickSearch,
    onSort,
    applyTagFilter,
    onFiltersChange,
    debounceSearch,
    ordersProviderFn,
    ordersProviderMethodsObj
  };
}
